import {
  Alert,
  AlertDescription,
  AlertTitle,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@invent/wl-ui-kit-next/ui';
import { Trash2 } from 'lucide-react';
import { Outlet, useLocation, useOutletContext, useParams } from 'react-router-dom';

import { useRecordVisit } from '@/design';
import type { IEtagged } from '@/domains/api';
import { formatDateTime } from '@/domains/survey';
import { oversightApi } from '../api/oversight-api';
import type { IOversightResponseDetail } from '../api/oversight-contracts';
import {
  AdminPage,
  Fact,
  LoadError,
  TableSkeleton,
  useAdminData
} from '../components/admin-page';
import { CopyableValue } from '../components/copyable-value';
import { BackToList, ParticipantLink, searchFrom } from '../components/participant-link';
import { RecyclePress } from '../components/recycle-press';
import { ResponseStatusChip } from '../components/responses-table';
import { TEAM_PATHS, responsePath } from '../paths';
import { inTheBin, subjectOf } from '../recycle-bin';

/**
 * One response, as staff oversee it — read, and **corrected**, never filled in (SR-222's F4). A
 * correction is survey-api's write, not a respondent's: a submitted or locked response's answer
 * changed in place on the Answers pane (the roadmap's U5 — the hub links here and never draws the
 * questionnaire), one address at a time, with a reason and the revision this read's `ETag` gave as
 * `If-Match`. It carries no `baseRevision` from a respondent's tab and opens no draft: the status
 * never changes, and the audit says who, when and why (`answer_corrected` on the Timeline pane).
 * A correction's 200 is the whole detail with its new `ETag`, which `adopt` takes as this read.
 *
 * What still never happens here: the rule engine is not run, no finding is named, and `revision`
 * stays staff's fact — no respondent sees it. A `null` `ETag` (an API from before SR-222) turns
 * corrections off on the Answers pane and leaves the page as it was.
 *
 * **The recycle bin's press is the header's** (SR-470's D3, `RecyclePress`): "Move to recycle bin…"
 * or "Restore…", whose 200 — the detail and its `ETag` — is adopted as the read, as a
 * correction's is. A recycled response says so at the top, from which status it was moved, when
 * and by whom, and that a restore comes before a correction: the Answers pane offers none off
 * `submitted` and `locked`.
 *
 * The four panes are routes rather than component state, for the reason the metadata
 * section's are: a reviewer's link to the timeline of a response has to survive being
 * pasted to someone else. The detail is loaded **here**, once, and handed down — React
 * Router keeps this element mounted while only the child changes, so switching panes
 * re-fetches nothing, and the panes that need only an id (timeline, flags, notes) do not
 * pull the whole questionnaire a second time.
 *
 * Opened from the list, it is drawn beside it (`AdminResponsesArea`), and its way back — and Esc —
 * returns to the list under the filters it was opened from; the routed tabs carry that along.
 *
 * The participant it is about is a link to their hub — every number, account, registration,
 * response and staff note the product holds for them and their household.
 *
 * **It remounts on the response** — the hub's lesson. The split view keeps this element mounted
 * when another row is opened, so without the key the first response's answers stayed on screen,
 * pencils live and a draft open, under the second one's address until its read landed (for good,
 * if it failed); and what the Answers pane keeps per address — a reason, a sentence — carried
 * over to the same address of another response.
 */
export function AdminResponseViewPage() {
  const { responseId = '' } = useParams();
  return <ResponseView key={responseId} responseId={responseId} />;
}

function ResponseView({ responseId }: { responseId: string }) {
  const { state } = useLocation();
  const { data, error, reload, adopt } = useAdminData<IEtagged<IOversightResponseDetail>>(
    (signal) => oversightApi.getResponse(responseId, signal),
    [responseId]
  );
  const response = data?.body ?? null;
  const etag = data?.etag ?? null;

  const base = responsePath(responseId);
  useRecordVisit(
    response
      ? {
          kind: 'response',
          path: responsePath(response.responseId),
          label: `${response.participant.displayName}, ${response.survey.code} ${response.cycle.code}`
        }
      : null
  );

  return (
    <AdminPage
      title={response ? response.participant.displayName : 'Response'}
      crumb="Responses"
      actions={
        response && (
          <RecyclePress
            response={{
              responseId: response.responseId,
              status: response.status,
              // An API that sends no `ETag` still quotes the revision this read gave.
              etag: etag ?? `"${response.revision}"`,
              recycled: response.recycled,
              subject: subjectOf(response)
            }}
            onMoved={async (moved) => {
              adopt(moved);
              return true;
            }}
            onReread={reload}
          />
        )
      }
      tabs={[
        { to: base, label: 'Answers', end: true },
        { to: `${base}/timeline`, label: 'Timeline' },
        { to: `${base}/flags`, label: 'Review flags' },
        { to: `${base}/notes`, label: 'Staff notes' }
      ]}
    >
      <BackToList
        list={TEAM_PATHS.responses}
        search={searchFrom(state)}
        all="All responses"
        state={state}
      />

      {error &&
        (data === null ? (
          <LoadError message={error} />
        ) : (
          // A response already on screen stays, as last read — a correction's re-read that failed
          // is not the first load failing.
          <Alert variant="destructive">
            <AlertTitle>Could not refresh — showing the response as last read</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ))}

      {response === null ? (
        <TableSkeleton rows={5} />
      ) : (
        <>
          {response.status === 'recycled' && (
            <Alert data-recycled-notice>
              <Trash2 className="size-4" aria-hidden />
              <AlertDescription>
                {inTheBin(response.recycled, true)}{' '}
                Restore it to correct an answer.
              </AlertDescription>
            </Alert>
          )}
          <Card>
            <CardHeader>
              <CardTitle>
                {response.survey.name}{' '}
                <span className="ml-1 align-middle">
                  <ResponseStatusChip status={response.status} />
                </span>
              </CardTitle>
              <CardDescription>
                {response.cycle.name} · version {response.version.versionNo}
                {response.path && ` · path ${response.path.name}`}
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-3 text-sm sm:grid-cols-3">
              <Fact
                label="Response id"
                value={<CopyableValue value={response.responseId} name="Copy the response id" />}
              />
              <Fact label="Revision" value={String(response.revision)} />
              <Fact label="Open flags" value={String(response.openFlags)} />
              <Fact
                label="Participant"
                value={
                  <>
                    <ParticipantLink participant={response.participant} />{' '}
                    {`(${response.participant.kind})`}
                  </>
                }
              />
              <Fact
                label="Submitted"
                value={response.submittedAt ? formatDateTime(response.submittedAt) : 'not yet'}
              />
              <Fact
                label="Locked"
                value={response.lockedAt ? formatDateTime(response.lockedAt) : 'no'}
              />
            </CardContent>
          </Card>

          <Outlet context={{ response, etag, reload, adopt } satisfies IResponseContext} />
        </>
      )}
    </AdminPage>
  );
}

export interface IResponseContext {
  response: IOversightResponseDetail;
  /** The detail's `ETag` — the revision a correction quotes back verbatim as `If-Match`; `null`
   *  when survey-api sent none, which turns corrections off (never "no precondition"). */
  etag: Nullable<string>;
  /** Re-reads the detail — the flags panes change `openFlags` on the card above. Resolves `false`
   *  when the newest read failed. */
  reload: () => Promise<boolean>;
  /** Takes a correction's answer — the whole detail and its new `ETag` — as the read. */
  adopt: (next: IEtagged<IOversightResponseDetail>) => void;
}

/** The panes' access to the detail this shell already fetched. */
export function useResponseContext(): IResponseContext {
  return useOutletContext<IResponseContext>();
}
